import React, { useState, useEffect } from 'react';
import { 
    Dialog, DialogTitle, DialogContent, DialogActions, Button, 
    TextField, Autocomplete, Box, Typography, Snackbar, Alert, MenuItem
} from '@mui/material';
import * as API from '../../api';
import CurrencyInput from '../CurrencyInput';

const CustomLabel = ({ title, required = false }) => (
    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5, color: '#2c3e50', fontSize: '0.9rem' }}>
        {title} {required && <span style={{ color: 'red' }}>*</span>}
    </Typography>
);

const TOUR_CATEGORIES = [
  { value: 'CRUISE', label: 'Du thuyền (Cruise)' },
  { value: 'DAILY', label: 'Tour ghép hàng ngày' },
  { value: 'PACKAGE', label: 'Tour trọn gói' },
  { value: 'ADVENTURE', label: 'Khám phá - Mạo hiểm' },
  { value: 'ECO_CULTURE', label: 'Sinh thái - Văn hóa' },
  { value: 'FREE_EASY', label: 'Combo (Free & Easy)' },
  { value: 'OTHER', label: 'Khác' },
];

const TourDialog = ({ open, onClose, initialData, onSuccess }) => {
    // Thêm các trường _original, _currency, _rate vào state để hỗ trợ nhập liệu USD
    const [form, setForm] = useState({
        tour_code: '',
        name: '',
        supplier: null,
        location: null,
        duration_days: 1,
        base_tour_name: '',
        transport_option: '',
        itinerary: null,
        
        // Giá gốc (Cost)
        cost_price: 0,
        cost_original: 0,
        cost_currency: 'VND',
        cost_rate: 1,

        // Giá bán (Selling)
        base_selling_price: 0,
        base_selling_original: 0,
        base_selling_currency: 'VND',
        base_selling_rate: 1
    });

    const [suppliers, setSuppliers] = useState([]);
    const [locations, setLocations] = useState([]);
    const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

    const showToast = (msg, severity = 'success') => setToast({ open: true, message: msg, severity });
    const handleCloseToast = () => setToast({ ...toast, open: false });

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [supRes, locRes] = await Promise.all([
                    API.fetchTransportProviders(),
                    API.fetchLocations()
                ]);
                setSuppliers(supRes.data || []);
                setLocations(locRes.data || []);
            } catch (err) {
                console.error("Lỗi tải danh mục:", err);
            }
        };
        fetchData();
    }, []);

    useEffect(() => {
        if (initialData) {
            // Khi sửa, mặc định load lên là VND vì DB không lưu currency của Tour
            setForm({
                ...initialData,
                supplier: initialData.supplier || null,
                location: initialData.location || null,
                itinerary: initialData.itinerary || null,
                
                // Map lại dữ liệu tiền tệ mặc định
                cost_original: initialData.cost_price || 0,
                cost_currency: 'VND',
                cost_rate: 1,
                
                base_selling_original: initialData.base_selling_price || 0,
                base_selling_currency: 'VND',
                base_selling_rate: 1
            });
        } else {
            // Reset form khi thêm mới
            setForm({
                tour_code: '',
                name: '',
                supplier: null,
                location: null,
                duration_days: 1,
                base_tour_name: '',
                transport_option: '',
                itinerary: null,
                tour_category: 'OTHER',
                cost_price: 0, cost_original: 0, cost_currency: 'VND', cost_rate: 1,
                base_selling_price: 0, base_selling_original: 0, base_selling_currency: 'VND', base_selling_rate: 1
            });
        }
    }, [initialData, open]);

    const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

    // Hàm xử lý chung cho thay đổi tiền tệ
    const handleMoneyChange = (fieldPrefix, data) => {
        setForm(prev => ({
            ...prev,
            [`${fieldPrefix}_price`]: data.vnd,
            [`${fieldPrefix}_original`]: data.original,
            [`${fieldPrefix}_currency`]: data.currency,
            [`${fieldPrefix}_rate`]: data.rate
        }));
    };

    const handleSubmit = async () => {
        if (!form.tour_code || !form.name) {
            showToast("Vui lòng nhập Mã Tour và Tên Tour!", "error");
            return;
        }

        // Chuyển sang FormData để hỗ trợ upload File
        const fd = new FormData();
        fd.append('name', form.name);
        if (!initialData) fd.append('tour_code', form.tour_code);
        if (form.tour_category) fd.append('tour_category', form.tour_category);
        if (form.base_tour_name) fd.append('base_tour_name', form.base_tour_name);
        if (form.transport_option) fd.append('transport_option', form.transport_option);
        fd.append('duration_days', form.duration_days);
        
        fd.append('cost_price', form.cost_price);
        fd.append('base_selling_price', form.base_selling_price);

        if (form.supplier) fd.append('supplier', typeof form.supplier === 'object' ? form.supplier.id : form.supplier);
        if (form.location) fd.append('location', typeof form.location === 'object' ? form.location.id : form.location);

        // Append file nếu có
        if (form.itinerary instanceof File) {
            fd.append('itinerary', form.itinerary);
        }

        try {
            // Mặc định axios sẽ tự cấu hình multipart/form-data khi nhận FormData
            if (initialData?.tour_code) await API.updateTour(initialData.tour_code, fd);
            else await API.createTour(fd);
            
            showToast("Lưu tour thành công!", "success");
            setTimeout(() => onSuccess(), 500);
        } catch (e) {
            const errMsg = e.response?.data?.detail || e.message;
            showToast('Lỗi lưu tour: ' + errMsg, "error");
        }
    };

    const rowStyle = { display: 'flex', gap: 2, width: '100%', marginBottom: 1 };
    const itemStyle = { flex: 1 };

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
                <DialogTitle sx={{ bgcolor: '#007373', color: 'white', fontWeight: 'bold' }}>
                    {initialData ? 'CẬP NHẬT THÔNG TIN TOUR' : 'THÊM MỚI TOUR'}
                </DialogTitle>
                <DialogContent dividers>
                    <Box>
                        {/* DÒNG 1 */}
                        <Box sx={rowStyle}>
                            <Box sx={itemStyle}>
                                <CustomLabel title="Tên Tour đầy đủ" required />
                                <TextField name="name" size="small" fullWidth required 
                                    placeholder="VD: Halong Odyssey Cruise 1 Day No Transfer" 
                                    value={form.name} onChange={handleChange} />
                            </Box>
                            <Box sx={itemStyle}>
                                <CustomLabel title="Mã Tour (Unique)" required />
                                <TextField name="tour_code" size="small" fullWidth required 
                                    placeholder="VD: OD-1D-NT" value={form.tour_code} 
                                    onChange={handleChange} disabled={!!initialData} />
                            </Box>
                        </Box>

                        {/* DÒNG 2 */}
                        <Box sx={rowStyle}>
                            <Box sx={itemStyle}>
                                <CustomLabel title="Loại hình Tour" />
                                <TextField name="tour_category" size="small" fullWidth select
                                    value={form.tour_category || 'OTHER'}
                                    onChange={handleChange}>
                                {TOUR_CATEGORIES.map((cat) => (
                                    <MenuItem key={cat.value} value={cat.value}>
                                    {cat.label}
                                    </MenuItem>
                                ))}
                                </TextField>
                            </Box>
                            <Box sx={itemStyle}>
                                <CustomLabel title="Tên nhóm (Base Name)" />
                                <TextField name="base_tour_name" size="small" fullWidth 
                                    placeholder="VD: Halong Odyssey Cruise" value={form.base_tour_name} 
                                    onChange={handleChange} />
                            </Box>
                        </Box>

                        {/* DÒNG 3 */}
                        <Box sx={rowStyle}>
                            <Box sx={itemStyle}>
                                <CustomLabel title="Nhà cung cấp" />
                                <Autocomplete size="small" options={suppliers} 
                                    getOptionLabel={(opt) => opt.name || ''}
                                    value={suppliers.find(s => s.id === form.supplier) || null}
                                    onChange={(e, val) => setForm({...form, supplier: val ? val.id : null})}
                                    renderInput={(params) => <TextField {...params} placeholder="Chọn NCC..." />} />
                            </Box>
                            <Box sx={itemStyle}>
                                <CustomLabel title="Địa điểm" />
                                <Autocomplete size="small" options={locations} 
                                    getOptionLabel={(opt) => opt.name || ''}
                                    value={locations.find(l => l.id === form.location) || null}
                                    onChange={(e, val) => setForm({...form, location: val ? val.id : null})}
                                    renderInput={(params) => <TextField {...params} placeholder="Chọn địa điểm..." />} />
                            </Box>
                        </Box>

                        {/* DÒNG 4 */}
                        <Box sx={rowStyle}>
                            <Box sx={itemStyle}>
                                <CustomLabel title="Thời lượng (ngày)" />
                                <TextField name="duration_days" type="number" size="small" fullWidth 
                                    value={form.duration_days} onChange={handleChange} />
                            </Box>
                            <Box sx={itemStyle}>
                                <CustomLabel title="Tùy chọn vận chuyển" />
                                <TextField name="transport_option" size="small" fullWidth 
                                    placeholder="VD: Limousine / Shuttle Bus" 
                                    value={form.transport_option} onChange={handleChange} />
                            </Box>
                        </Box>

                        {/* THÊM MỚI: DÒNG 5 - UPLOAD ITINERARY */}
                        <Box sx={rowStyle}>
                            <Box sx={itemStyle}>
                                <CustomLabel title="Lịch trình Tour (PDF/Doc/Image)" />
                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                    <Button variant="outlined" component="label" size="small">
                                        Tải lên Lịch trình
                                        <input type="file" hidden accept=".pdf,.doc,.docx,image/*" onChange={e => {
                                            if(e.target.files[0]) setForm({...form, itinerary: e.target.files[0]});
                                        }} />
                                    </Button>
                                    {form.itinerary && (
                                        <Button 
                                            size="small" color="success" 
                                            href={form.itinerary instanceof File ? URL.createObjectURL(form.itinerary) : form.itinerary} 
                                            target="_blank"
                                        >
                                            Xem Lịch trình hiện tại
                                        </Button>
                                    )}
                                </Box>
                            </Box>
                            <Box sx={itemStyle}></Box> {/* Cột rỗng để cân đối bố cục */}
                        </Box>

                        {/* DÒNG 5: ĐÃ SỬA LẠI PHẦN TIỀN TỆ */}
                        <Box sx={{ ...rowStyle, marginBottom: 0 }}>
                            <Box sx={itemStyle}>
                                <CustomLabel title="Giá Net (Cost)" />
                                <CurrencyInput 
                                    value={{
                                        original: form.cost_original,
                                        currency: form.cost_currency,
                                        rate: form.cost_rate,
                                        vnd: form.cost_price
                                    }}
                                    onChange={(data) => handleMoneyChange('cost', data)}
                                />
                            </Box>
                            <Box sx={itemStyle}>
                                <CustomLabel title="Giá Bán lẻ đề xuất" />
                                <CurrencyInput 
                                    value={{
                                        original: form.base_selling_original,
                                        currency: form.base_selling_currency,
                                        rate: form.base_selling_rate,
                                        vnd: form.base_selling_price
                                    }}
                                    onChange={(data) => handleMoneyChange('base_selling', data)}
                                />
                            </Box>
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={onClose} variant="outlined" color="inherit">Hủy</Button>
                    <Button onClick={handleSubmit} variant="contained" sx={{ bgcolor: '#007373' }}>Lưu dữ liệu</Button>
                </DialogActions>
            </Dialog>
            <Snackbar open={toast.open} autoHideDuration={2000} onClose={handleCloseToast} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
                <Alert onClose={handleCloseToast} severity={toast.severity} sx={{ width: '100%', boxShadow: 3 }} variant="filled">{toast.message}</Alert>
            </Snackbar>
        </>
    );
};

export default TourDialog;